/**
 * Hex floor renderer (PRD §5.2).
 *
 * Each tile is a hex prism extruded down to a shared floor plane, with
 * height driven by `TerrainHex.elevation + TerrainSpec.baseLift`. This
 * gives cliffs that read at neighbor boundaries instead of disconnected
 * floating disks. Water tiles render with depressed elevation + an
 * emissive caustic.
 *
 * Decorations (forest sprigs, rubble piles, rocks, water caustic) paint
 * on the tile's top face, seeded from `(map_seed, q, r, terrain_id)`
 * so the same map renders identically twice.
 *
 * Adding a terrain type: append to `TERRAIN_CATALOG` in `voxel/
 * terrain/catalog.ts` (+ optionally a decoration in `decorations.ts`).
 * No edits in this file.
 */

import { useMemo } from "react";
import { Color, InstancedMesh, Matrix4 } from "three";
import type { BattleState, TerrainHex } from "@pilot-protocol/replay-parser";
import {
  HEX_SIZE,
  TILE_THICKNESS,
  hexToWorld,
  ringInRadius,
} from "./hexMath";
import { SeededRng } from "../determinism/cosmeticSeed";
import {
  TERRAIN_CATALOG,
  TERRAIN_FALLBACK,
  specForTerrain,
  type TerrainSpec,
} from "../voxel/terrain/catalog";
import {
  DECORATIONS,
  type DecorationKind,
} from "../voxel/terrain/decorations";
import type { Voxel, MaterialSlot } from "../voxel/types";
import { useEffect, useRef } from "react";

interface HexFloorProps {
  state: BattleState;
  /** Optional 32-bit seed so per-tile decoration scatter is reproducible. */
  mapSeed?: number;
}

// Decoration voxel size relative to the tile — small enough to read as
// surface detail, large enough to register at standard zoom. Scaled to
// match the mech voxel grid (0.025 world units / voxel).
const DECOR_VOXEL = 0.025;

export function HexFloor({ state, mapSeed = 0 }: HexFloorProps) {
  const tiles = useMemo(() => deriveTiles(state), [state]);
  const decorBuckets = useMemo(() => computeDecorations(tiles, mapSeed), [tiles, mapSeed]);

  return (
    <group name="hex-floor">
      {tiles.map((t) => {
        const spec = specForTerrain(t.type);
        const totalY = (t.elevation + spec.baseLift) * TILE_THICKNESS;
        // Extrusion height: from a fixed floor plane up to the tile's
        // top surface. The cylinder is positioned so its top face sits
        // at `totalY` and it extends down to `FLOOR_Y` (-1 tile).
        const height =
          spec.elevationRender === "depressed"
            ? TILE_THICKNESS
            : Math.max(TILE_THICKNESS, totalY - FLOOR_Y);
        const centerY =
          spec.elevationRender === "depressed"
            ? totalY - height / 2
            : FLOOR_Y + height / 2;
        const w = hexToWorld(t.position, 0);
        return (
          <mesh
            key={`${t.position.q},${t.position.r}`}
            receiveShadow
            castShadow
            position={[w.x, centerY, w.z]}
            rotation={[0, Math.PI / 6, 0]}
          >
            <cylinderGeometry
              args={[HEX_SIZE * 0.97, HEX_SIZE * 0.97, height, 6]}
            />
            <meshStandardMaterial
              color={spec.color}
              roughness={spec.roughness}
              metalness={spec.metalness}
              emissive={spec.emissive ? spec.color : "#000000"}
              emissiveIntensity={spec.emissive ? 0.4 : 0}
            />
          </mesh>
        );
      })}
      {(["primary", "secondary", "accent", "emissive"] as MaterialSlot[]).map((slot) => (
        <DecorationInstances key={slot} slot={slot} voxels={decorBuckets[slot]} />
      ))}
    </group>
  );
}

const FLOOR_Y = -1; // anchor plane the tiles extrude down to

/**
 * `state.terrain` is a SPARSE list of per-hex overrides from the engine
 * (only the tagged tiles ship on the wire). The viewer paints the full
 * radius-`map_radius` disc, so anything not in `state.terrain` falls
 * through to a plain default tile (type 0, elevation 0). Without this
 * union, every untagged hex would be invisible — that was the bug
 * caught on the iter17 brawler-vs-support map (most of the disc missing
 * even though only ~20 terrain rows shipped).
 */
export function deriveTiles(state: BattleState): TerrainHex[] {
  const tagged = new Map<string, TerrainHex>();
  for (const t of state.terrain ?? []) {
    tagged.set(`${t.position.q},${t.position.r}`, t);
  }
  return ringInRadius(state.map_radius).map((p) => {
    const key = `${p.q},${p.r}`;
    const override = tagged.get(key);
    return (
      override ?? {
        position: p,
        type: 0,
        elevation: 0,
      }
    );
  });
}

/**
 * Walk each terrain tile, seeded-pick decoration voxels per-hex, and
 * collapse into per-material-slot buckets so we can render with one
 * `InstancedMesh` per slot (4 draw calls total for the entire floor's
 * decorations, regardless of tile count).
 */
function computeDecorations(
  tiles: TerrainHex[],
  mapSeed: number,
): Record<MaterialSlot, Array<Voxel & { wx: number; wy: number; wz: number; color: string }>> {
  const buckets: Record<
    MaterialSlot,
    Array<Voxel & { wx: number; wy: number; wz: number; color: string }>
  > = {
    primary: [],
    secondary: [],
    accent: [],
    emissive: [],
  };

  for (const tile of tiles) {
    const spec = specForTerrain(tile.type);
    if (!spec.decoration) continue;

    const rng = new SeededRng(hashTileSeed(mapSeed, tile.position.q, tile.position.r, tile.type));
    const voxels = DECORATIONS[spec.decoration as DecorationKind](rng);

    const w = hexToWorld(tile.position, 0);
    const topY =
      spec.elevationRender === "depressed"
        ? (tile.elevation + spec.baseLift) * TILE_THICKNESS
        : (tile.elevation + spec.baseLift) * TILE_THICKNESS;

    for (const v of voxels) {
      buckets[v.slot].push({
        ...v,
        wx: w.x + v.x * DECOR_VOXEL,
        wy: topY + (v.y + 0.5) * DECOR_VOXEL,
        wz: w.z + v.z * DECOR_VOXEL,
        color: decorationColorFor(spec, v.slot),
      });
    }
  }

  return buckets;
}

function decorationColorFor(spec: TerrainSpec, slot: MaterialSlot): string {
  // Decoration palette uses a tasteful mapping per slot. Primary stays
  // tile-color (so rocky outcrops on ridges blend), secondary darkens
  // for tree trunks / shadows, accent is the tile-specific decoration
  // color, emissive is shared bright orange (matches mech aesthetic).
  switch (slot) {
    case "primary":
      return spec.color;
    case "secondary":
      return "#1a1a1a";
    case "accent":
      // Forest gets a leafier green, rubble gets dust-tan, etc.
      if (spec.decoration === "forest_sprig") return "#4a7a3a";
      if (spec.decoration === "rubble_pile") return "#6a5a44";
      if (spec.decoration === "rocky_outcrop") return "#8a8678";
      return spec.color;
    case "emissive":
      return spec.emissive ? "#88ddff" : "#ff6a1c";
  }
}

interface DecorationInstancesProps {
  slot: MaterialSlot;
  voxels: ReadonlyArray<{ wx: number; wy: number; wz: number; color: string }>;
}

function DecorationInstances({ slot, voxels }: DecorationInstancesProps) {
  const meshRef = useRef<InstancedMesh>(null);
  const count = voxels.length;

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const m = new Matrix4();
    const tmpColor = new Color();
    voxels.forEach((v, i) => {
      m.makeTranslation(v.wx, v.wy, v.wz);
      mesh.setMatrixAt(i, m);
      tmpColor.set(v.color);
      mesh.setColorAt(i, tmpColor);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  }, [voxels]);

  if (count === 0) return null;

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]} castShadow receiveShadow>
      <boxGeometry args={[DECOR_VOXEL, DECOR_VOXEL, DECOR_VOXEL]} />
      <meshStandardMaterial
        vertexColors
        roughness={slot === "emissive" ? 0.4 : 0.85}
        metalness={slot === "secondary" ? 0.3 : 0.1}
        emissive={slot === "emissive" ? "#ffffff" : "#000000"}
        emissiveIntensity={slot === "emissive" ? 1.2 : 0}
        toneMapped={slot !== "emissive"}
      />
    </instancedMesh>
  );
}

function hashTileSeed(mapSeed: number, q: number, r: number, type: number): number {
  let h = mapSeed >>> 0;
  h = (Math.imul(h ^ q, 0x85ebca6b) >>> 0);
  h = (Math.imul(h ^ r, 0xc2b2ae35) >>> 0);
  h = (Math.imul(h ^ type, 0x27d4eb2f) >>> 0);
  return h >>> 0;
}

// Back-compat exports — consumers using the old palette helper still
// resolve through the catalog.
export { TERRAIN_CATALOG, TERRAIN_FALLBACK };
